import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  ViewChild,
  ViewEncapsulation,
} from "@angular/core";
/**
 * Vattenfall UI `ui-truncate` text truncation component.
 *
 * Wrapping a text with `ui-truncate` will collapse the text to the number of rows specified by the input. Default is 2 rows.
 * This component uses -webkit-box and -webkit-line-clamp to implement truncation.
 */
@Component({
  selector: "ui-truncate",
  templateUrl: "./truncate.component.html",
  styleUrls: ["./truncate.component.scss"],
  encapsulation: ViewEncapsulation.Emulated,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TruncateComponent implements AfterViewInit {
  constructor(protected changeDetectorRef: ChangeDetectorRef) {}
  /** Number of rows to show initaly Default is 2. */
  @Input() rows = 2;
  /**
   * The text element that we need to truncate.
   */
  @ViewChild("text")
  private text!: ElementRef<HTMLElement>;
  /**
   * If content is exandable. Default is true.
   * If the number of rows in the text is less than the max rows to show, then we dont need to show expand and collapse buttons.
   */
  expandable = true;
  /**
   * Tracks the current state of the content.
   * Default value false.
   */
  expanded = false;

  ngAfterViewInit(): void {
    /**
     * We are using resize Observer to do the initial truncation of the text.
     * The reason being that we only want to truncate text after it has already been rendered and the height of the text element is now known to the borwser.
     * This observer was introduced to fix the bug of text not being truncated by default when loaded (usually the first time we use truncate component would not truncate in initialisation).
     * */
    const resizeObserver = new ResizeObserver(() => {
      this.truncate();
      // Disconnect as soon as we truncate for the first time. From now on, its the user interactions that truncates/expands text.
      resizeObserver.disconnect();
    });
    // Listen to resize of text element.
    resizeObserver.observe(this.text.nativeElement);
  }
  /**
   *
   * @returns the clamp style with a given number of lines to clamp.
   */
  private getClampStyle(): Record<string, string> {
    return {
      overflow: "hidden",
      display: "-webkit-box",
      "-webkit-box-orient": "vertical",
      "-webkit-line-clamp": this.rows.toString(),
    };
  }
  /**
   * This function will count the total number of lines from a given html element.
   * @returns total number of lines as number
   */
  private countNumberOfLines(): number {
    const textElement: HTMLElement = this.text.nativeElement;
    const computedStyle: CSSStyleDeclaration = window.getComputedStyle(textElement);
    /**
     * By removing padding top/bottom - we can calculate the number of lines in a given element by measuring its height and dividing it by the line-height.
     */
    const [lineHeightStyle, contentHeightStyle, contentPaddingTop, contentPaddingBottom] = [
      parseInt(computedStyle?.getPropertyValue("line-height")) || 1.2,
      parseInt(computedStyle?.getPropertyValue("height")),
      parseInt(computedStyle?.getPropertyValue("padding-top")),
      parseInt(computedStyle?.getPropertyValue("padding-bottom")),
    ];
    const lineHeight: number = lineHeightStyle;
    const contentHeight = contentHeightStyle - contentPaddingTop - contentPaddingBottom;
    const totalNumberOrRows = Math.round(contentHeight / lineHeight);
    return totalNumberOrRows;
  }
  /**
   * This function will collapse the given element by adding
   */
  truncate() {
    const textElement = this.text.nativeElement;
    // set -webkit-line-clamp to the number of rows input value
    Object.assign(textElement.style, this.getClampStyle());
    this.expanded = false;
    this.changeDetectorRef.detectChanges();
    /** Set the flag expandable if the number of rows in the text is higher than the rows we want to show. */
    this.expandable = this.countNumberOfLines() > this.rows;
  }
  /**
   * Clean the inline style applied by the collapse() function above.
   */
  expand() {
    // clean the expand style added as style attribute.
    const textElement: HTMLElement = this.text.nativeElement;
    const clampStyle = this.getClampStyle();
    const properties: string[] = Object.keys(clampStyle);
    // loop over each property and remove it from the text element.
    properties.map((key) => {
      textElement.style.removeProperty(key);
    });
    this.expanded = true;
    this.changeDetectorRef.detectChanges();
  }
}
